import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";
import type { ApiResponse } from "../../features/leaderboard/types/index.ts";
import type { AuditStatusPayload } from "../../features/project-detail/types/index.ts";
import { apiRequest } from "../lib/api-client.ts";
import { queryKeys } from "../lib/query-keys.ts";
import {
  ACTIVE_POLL_INTERVAL_MS,
  getScoringTaskPollInterval,
  isAuditTerminalStatus,
  isScoringTaskActive,
  parseCommunityStatus,
  type ScoringBatchStatusItem,
  type ScoringProgress,
  type TaskStatus,
  type Toast,
} from "../types/scoring.ts";

interface UseScoringPollingParams {
  scoringProgressMap: Record<string, ScoringProgress>;
  setScoringProgressMap: React.Dispatch<React.SetStateAction<Record<string, ScoringProgress>>>;
  selectedProjectId: string | null;
  addToast: (type: Toast["type"], title: string, message: string) => void;
}

export function useScoringPolling({
  scoringProgressMap,
  setScoringProgressMap,
  selectedProjectId,
  addToast,
}: UseScoringPollingParams) {
  const queryClient = useQueryClient();
  const scoringPollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const selectedProjectIdRef = useRef(selectedProjectId);
  selectedProjectIdRef.current = selectedProjectId;

  const refreshProjectRow = useCallback(
    async (projectId: string) => {
      try {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: queryKeys.leaderboard.projectsRoot() }),
          queryClient.invalidateQueries({ queryKey: queryKeys.leaderboard.project(projectId) }),
        ]);
      } catch (err) {
        console.error(`Failed to refresh project ${projectId}:`, err);
      }
    },
    [queryClient]
  );

  const refreshModalData = useCallback(
    async (projectId: string) => {
      try {
        await queryClient.invalidateQueries({
          queryKey: queryKeys.projectDetail.detail(projectId),
        });
      } catch (err) {
        console.error("Failed to refresh modal data:", err);
      }
    },
    [queryClient]
  );

  const pollScoringTasks = useCallback(async () => {
    const now = Date.now();
    const tasks = Object.values(scoringProgressMap);
    const dueTasks = tasks
      .filter((task) => isScoringTaskActive(task))
      .filter((task) => now - task.lastPolledAt >= getScoringTaskPollInterval(task));

    if (dueTasks.length === 0) return;

    const statusByProject = new Map<
      string,
      {
        audit: AuditStatusPayload | null;
        community: ReturnType<typeof parseCommunityStatus> | null;
      }
    >();

    try {
      const batchRes = await apiRequest("/v1/projects/status/batch", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ projectIds: dueTasks.map((task) => task.projectId) }),
      });

      if (!batchRes.ok) {
        throw new Error(`Batch status polling failed with ${batchRes.status}`);
      }

      const payload = (await batchRes.json()) as ApiResponse<ScoringBatchStatusItem[]>;
      for (const item of payload.data ?? []) {
        const currentTask = dueTasks.find((task) => task.projectId === item.projectId);
        statusByProject.set(item.projectId, {
          audit: item.audit,
          community: currentTask
            ? parseCommunityStatus(
                item.community ?? undefined,
                currentTask.community?.status ?? "none"
              )
            : null,
        });
      }
    } catch (err) {
      console.error("Failed to batch poll scoring status:", err);
    }

    const finalizedAudits: Array<{
      projectId: string;
      handle: string;
      status: TaskStatus;
      score?: number;
      providerFailCount: number;
    }> = [];

    setScoringProgressMap((prev) => {
      const next = { ...prev };

      for (const task of dueTasks) {
        const current = prev[task.projectId];
        if (!current) continue;

        const polled = statusByProject.get(task.projectId);
        if (!polled) {
          next[task.projectId] = {
            ...current,
            lastPolledAt: now,
          };
          continue;
        }

        const audit = polled.audit;
        const nextCommunity = polled.community ?? current.community;

        let nextStatus: TaskStatus = current.status;
        if (audit?.status === "queued") nextStatus = "queued";
        if (audit?.status === "processing") nextStatus = "processing";
        if (audit?.status === "completed") nextStatus = "completed";
        if (audit?.status === "failed") nextStatus = "failed";
        if (audit?.status === "partial") nextStatus = "partial";

        const shouldUseAuditSnapshot =
          audit?.status === "completed" ||
          audit?.status === "failed" ||
          audit?.status === "partial";
        const completedProviders = shouldUseAuditSnapshot
          ? (audit?.providers?.completed ?? current.completedProviders)
          : current.completedProviders;
        const failedProviders = shouldUseAuditSnapshot
          ? (audit?.providers?.failed ?? current.failedProviders)
          : current.failedProviders;

        const updatedTask: ScoringProgress = {
          ...current,
          status: nextStatus,
          updatedAt: now,
          lastPolledAt: now,
          queueState: audit?.queueState ?? current.queueState,
          queuePosition: audit?.queuePosition ?? current.queuePosition,
          waitingMs: audit?.waitingMs ?? current.waitingMs,
          jobId: audit?.jobId ?? current.jobId,
          completedProviders,
          failedProviders,
          totalProviders: audit?.providers?.total ?? current.totalProviders,
          score:
            shouldUseAuditSnapshot &&
            audit?.latestRun?.alphaScore !== null &&
            audit?.latestRun?.alphaScore !== undefined
              ? Number(audit.latestRun.alphaScore)
              : current.score,
          confidence:
            shouldUseAuditSnapshot &&
            audit?.latestRun?.confidence !== null &&
            audit?.latestRun?.confidence !== undefined
              ? Number(audit.latestRun.confidence)
              : current.confidence,
          notes: shouldUseAuditSnapshot
            ? (audit?.latestRun?.notes ?? current.notes)
            : current.notes,
          providerScores: shouldUseAuditSnapshot
            ? (audit?.providers?.scores ?? current.providerScores)
            : current.providerScores,
          failedDetails: shouldUseAuditSnapshot
            ? (audit?.providers?.failedDetails ?? current.failedDetails)
            : current.failedDetails,
          backers: shouldUseAuditSnapshot ? (audit?.backers ?? current.backers) : current.backers,
          founder: shouldUseAuditSnapshot ? (audit?.founder ?? current.founder) : current.founder,
          community: nextCommunity,
        };

        const transitionedToTerminal =
          !isAuditTerminalStatus(current.status) && isAuditTerminalStatus(updatedTask.status);
        if (transitionedToTerminal) {
          finalizedAudits.push({
            projectId: current.projectId,
            handle: current.handle,
            status: updatedTask.status,
            score: updatedTask.score,
            providerFailCount: audit?.latestRun?.providerFailCount ?? failedProviders.length,
          });
        }

        next[task.projectId] = updatedTask;
      }

      return next;
    });

    for (const finalized of finalizedAudits) {
      void refreshProjectRow(finalized.projectId);

      if (selectedProjectIdRef.current === finalized.projectId) {
        void refreshModalData(finalized.projectId);
      }

      if (finalized.status === "completed") {
        if (finalized.providerFailCount > 0) {
          addToast(
            "info",
            `@${finalized.handle} scored`,
            `Score ${finalized.score?.toFixed(1) ?? "N/A"} with ${finalized.providerFailCount} provider failure${finalized.providerFailCount === 1 ? "" : "s"}`
          );
        } else {
          addToast(
            "success",
            `@${finalized.handle} scored`,
            `Score ${finalized.score?.toFixed(1) ?? "N/A"}`
          );
        }
      } else if (finalized.status === "partial") {
        addToast(
          "info",
          `@${finalized.handle} partial run`,
          "Critical providers failed. Keeping previous leaderboard score."
        );
      } else {
        addToast("error", `@${finalized.handle} failed`, "Audit run failed.");
      }
    }
  }, [scoringProgressMap, setScoringProgressMap, refreshProjectRow, refreshModalData, addToast]);

  useEffect(() => {
    const hasActiveScoringTasks = Object.values(scoringProgressMap).some((task) =>
      isScoringTaskActive(task)
    );

    if (!hasActiveScoringTasks) {
      if (scoringPollingRef.current) {
        clearInterval(scoringPollingRef.current);
        scoringPollingRef.current = null;
      }
      return;
    }

    void pollScoringTasks();
    scoringPollingRef.current = setInterval(() => {
      void pollScoringTasks();
    }, ACTIVE_POLL_INTERVAL_MS);

    return () => {
      if (scoringPollingRef.current) {
        clearInterval(scoringPollingRef.current);
        scoringPollingRef.current = null;
      }
    };
  }, [scoringProgressMap, pollScoringTasks]);

  useEffect(() => {
    return () => {
      if (scoringPollingRef.current) {
        clearInterval(scoringPollingRef.current);
      }
    };
  }, []);
}
